import styled from "@emotion/native"
import { Linking, Platform, ScrollView } from "react-native"
import { ButtonV2, Spacing, Text, Checkbox } from "../../ui-components"
import { Accordion } from "../Accordion.tsx"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper.tsx"
import { useState } from "react"
import { useAcceptTerms } from "../../api/mutations"

type TermsUIProps = {
  connectedBankName?: string
  handleSubmit?: (redirectUrl?: string) => void
  isContinueBtnDisabled?: boolean
  isLoading?: boolean
}

export function TermsUI({
  handleSubmit = () => null,
  isContinueBtnDisabled,
  isLoading,
}: TermsUIProps) {
  const [isTwilioTextChecked, setIsTwilioTextChecked] = useState(false)
  const { mutateAsync: acceptTerms, isPending: isAcceptingTerms } = useAcceptTerms()

  async function submit() {
    const response = await acceptTerms()
    handleSubmit(response.redirectUrl)
  }

  function forceBrowserOpen(url: string) {
    if (Platform.OS === "android") {
      Linking.openURL(`googlechrome://navigate?url=${url}`)
      return
    }

    Linking.openURL(url)
  }

  return (
    <OnboardingScreenWrapper stepNumber={5}>
      <Spacing top={12} />
      <Container>
        <ScrollView
          contentContainerStyle={{ paddingBottom: 20, paddingTop: 52, paddingHorizontal: 20 }}
        >
          <Text fontSize={36} lineHeight={43} fontFamily="IBMPlexSans-SemiBold">
            Know the terms
          </Text>
          <Spacing top={8} />
          <Text fontSize={20} lineHeight={32} fontFamily="IBMPlexSans-Regular" color="gray900">
            Understand everything legal
          </Text>
          <Spacing top={52} />
          <Accordions>
            <Accordion header="Terms & Conditions">
              <Text
                color="dodgerBlue"
                fontSize={16}
                lineHeight={22}
                fontFamily="IBMPlexSans-Medium"
                onPress={() => forceBrowserOpen("https://synchpay.com/terms-and-conditions/")}
              >
                SynchPay Terms and Conditions
              </Text>
            </Accordion>
            <Accordion header="Privacy Policy">
              <Text
                color="dodgerBlue"
                fontSize={16}
                lineHeight={22}
                fontFamily="IBMPlexSans-Medium"
                onPress={() => forceBrowserOpen("https://synchpay.com/privacy-policy/")}
              >
                SynchPay Privacy Policy
              </Text>
            </Accordion>
            <Accordion header="Customer Identification">
              <Text color="gray900" fontSize={16} lineHeight={22} fontFamily="IBMPlexSans-Regular">
                <Text>
                  To help the government fight the funding of terrorism and money laundering
                  activities, federal law requires all financial institutions to obtain, verify, and
                  record information that identifies each person opening an account.{"\n\n"}
                  That means before you create a SynchPay account, you’ll need to provide details
                  about yourself and any controlling shareholders, including name, address, date of
                  birth, taxpayer identification number, SSN/EIN, and/or any other documentation
                  that helps SynchPay verify identities.{"\n\n"}
                  We also may ask to see documentation verifying your identity, such as an unexpired
                  driver’s license or passport or, in the case of a business, a certified copy of
                  articles of incorporation and/or a government-issued business license, authorized
                  signers, account owners, and your officers, directors, beneficial owners,
                  controlling shareholders and others.{"\n\n"}
                  By joining SynchPay, you represent as of today’s date, and warrant at all times
                  until you have cancelled your SynchPay account, that neither you nor any
                  beneficial owner of your bank account is designated under or covered by any
                  sanctions program administered or enforced by the United States Department of the
                  Treasury’s Office of Foreign Assets Control. Please refer to our{" "}
                  <Text
                    color="dodgerBlue"
                    fontSize={16}
                    lineHeight={22}
                    fontFamily="IBMPlexSans-Medium"
                    onPress={() => forceBrowserOpen("https://synchpay.com/privacy-policy/")}
                  >
                    Privacy Policy{" "}
                  </Text>
                  and{" "}
                  <Text
                    color="dodgerBlue"
                    fontSize={16}
                    lineHeight={22}
                    fontFamily="IBMPlexSans-Medium"
                    onPress={() => forceBrowserOpen("https://synchpay.com/terms-and-conditions/")}
                  >
                    Terms of Service{" "}
                  </Text>
                  for more information.
                </Text>
              </Text>
            </Accordion>
            <Accordion header="Marketing" openByDefault>
              <TwilioTextContainer>
                <CheckboxContainer>
                  <Checkbox value={isTwilioTextChecked} onValueChange={setIsTwilioTextChecked} />
                </CheckboxContainer>
                <Text type="label" color="gray900">
                  I agree to receive time-sensitive notifications via text. I understand that I can
                  opt-out at any time by going to the settings in my app.
                </Text>
              </TwilioTextContainer>
            </Accordion>
          </Accordions>
          <Spacing top={52} />
          <Text color="gray900" fontSize={16} lineHeight={22} fontFamily="IBMPlexSans-Regular">
            <Text fontFamily="IBMPlexSans-SemiBold">ACH Authorization{"\n\n"}</Text>
            <Text>
              By agreeing to this Authorization SynchPay’s{" "}
              <Text
                color="dodgerBlue"
                fontFamily="IBMPlexSans-Medium"
                onPress={() => Linking.openURL("https://synchpay.com/terms-and-conditions/")}
              >
                Terms & Conditions{" "}
              </Text>
              , you authorize Balance Financial Technology, Inc. (“SynchPay”) to electronically
              debit and credit your designated deposit account at your designated depository
              financial institution (your “Bank Account”) via ACH on a one-off or recurring basis as
              often as needed to effectuate and, if ever applicable, to correct erroneous debits and
              credits via ACH as follows:{"\n"}
            </Text>
            <Text>
              - Amount: The amounts you instruct us to credit to or debit from your funding account
              when you initiate a payment request via SynchPay.{"\n"}- Frequency of Debits: multiple
              times per Business Day (however, debits and credits may not necessarily occur every
              Business Day){"\n\n"}
            </Text>
            <Text>
              You also acknowledge that the amount and frequency of the foregoing debits and credits
              may vary and that you waive your right to receive prior notice of the amount and date
              of each debit and credit.{"\n"}
            </Text>
            <Text>
              You acknowledge that the electronic authorization contained in this ACH Authorization
              represents your written authorization for ACH transactions as provided herein and will
              remain in full force and effect until you notify SynchPay that you wish to revoke this
              authorization by emailing Support@SynchPay.com. You must notify SynchPay at least 14
              Business Days before the scheduled debit date of any ACH transaction from your Bank
              Account in order to cancel this authorization. If we do not receive notice at least 14
              Business Days before the scheduled debit date, we may attempt, in our sole discretion,
              to cancel the debit transaction. However, we assume no responsibility for our failure
              to do so.{"\n"}
            </Text>
            <Text>
              You acknowledge that this agreement constitutes standing debit authorization and
              applies to any account you may link with SynchPay until such time as you revoke
              access.{"\n"}
            </Text>
            <Text>
              By providing your electronic consent to this Authorization, you 1) certify that you
              own and/or are authorized as a signer on the Bank Account and any subsequent Bank
              Account you designate to use to pay payment requests via SynchPay and 2) agree to be
              bound by the National Automated Clearing House Association (Nacha) Rules.{"\n"}
            </Text>
            <Text>
              By logging into your SynchPay account and affirmatively clicking or otherwise
              selecting the “Send Payment” button associated with the invoice or payment request,
              you affirmatively elect to pay the invoice and authorize the identified Merchant to
              initiate an ACH debit entry to your Bank Account for the amount listed as due on the
              invoice (each a “Subsequent Entry”).{"\n"}
            </Text>
            <Text>
              If you withdraw your electronic authorization contained in this ACH Authorization, we
              will suspend or close your SynchPay account, and you will no longer be able to use
              your SynchPay account or the Services, except as otherwise expressly provided in our
              terms of service at {"\n"}
              <Text
                color="dodgerBlue"
                fontFamily="IBMPlexSans-Medium"
                onPress={() => Linking.openURL("https://synchpay.com/terms-and-conditions/")}
              >
                Terms & Conditions{" "}
              </Text>
              .{"\n"}
            </Text>
            <Text>
              Please note that withdrawal of your electronic authorization contained in this ACH
              Authorization will not apply to transactions performed before the withdrawal of your
              authorization becomes effective.{"\n"}
            </Text>
            <Text>
              In addition to any of your other representations and warranties in this ACH
              Authorization, you represent that: (a) your browser is equipped with at least 128-bit
              security encryption; (b) you are capable of printing, storing, or otherwise saving a
              copy of this electronic authorization for your records; and (c) the ACH transactions
              you hereby authorize comply with applicable law.{"\n"}
            </Text>
          </Text>
        </ScrollView>
        <ButtonContainer>
          <Spacing top={20} left={20} right={20}>
            <ButtonV2
              title="Agree"
              variant="blue2"
              onPress={submit}
              disabled={isContinueBtnDisabled}
              loading={isAcceptingTerms || isLoading}
            />
          </Spacing>
        </ButtonContainer>
        {Platform.OS !== "ios" && <Spacing top={20} />}
      </Container>
    </OnboardingScreenWrapper>
  )
}

const Accordions = styled.View`
  gap: 24px;
`

const Container = styled.View`
  flex: 1;
  max-width: 500px;
  width: 100%;
  align-self: center;
  justify-content: space-between;
`

const CheckboxContainer = styled.View``

const ButtonContainer = styled.View`
  border-top-width: 2px;
  border-color: ${({ theme }) => theme.v2.screens.terms.buttonContainerBorderColor};
`

const TwilioTextContainer = styled.View`
  margin-top: 12px;
  flex-direction: row;
  gap: 8px;
  margin-right: 40px;
`
